// File manager component - browse, edit, upload, download files
// Uses its own WebSocket connection (independent of the terminal WS)

interface FileEntry {
  name: string;
  dir: boolean;
  size: number;
}

export interface FileManagerOptions {
  container: HTMLElement;
  wsUrl: string;
  token?: string;
  onClose: () => void;
}

export class FileManager {
  private container: HTMLElement;
  private ws: WebSocket;
  private onClose: () => void;
  private token?: string;
  private authenticated: boolean = false;
  private pendingMessages: string[] = [];
  private currentPath: string = '';
  private entries: FileEntry[] = [];
  private editorPath: string | null = null;
  private editorContent: string = '';
  private editorDirty: boolean = false;
  private originalContent: string = '';
  private pendingSave: { path: string; content: string } | null = null;
  private pendingDownloads: Set<string> = new Set();
  private closed: boolean = false;
  private destroyed: boolean = false;
  private el: HTMLElement;
  private keyHandler: (e: KeyboardEvent) => void;

  constructor(options: FileManagerOptions) {
    this.container = options.container;
    this.onClose = options.onClose;
    this.token = options.token;

    this.el = document.createElement('div');
    this.el.className = 'file-manager';
    this.el.innerHTML = '<div class="file-empty">Connecting...</div>';
    this.container.appendChild(this.el);

    // Create dedicated WebSocket connection
    this.ws = new WebSocket(options.wsUrl);

    this.ws.onopen = () => {
      if (this.token) {
        this.ws.send(JSON.stringify({ type: 'auth', token: this.token }));
      } else {
        this.authenticated = true;
        this.flushPending();
        this.navigate('');
      }
    };

    this.ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);
        this.handleMessage(msg);
      } catch (e) {
        console.error('[files] Failed to parse message:', e);
      }
    };

    this.ws.onclose = () => {
      console.log('[files] WebSocket closed');
      this.closed = true;
      this.authenticated = false;
      this.pendingMessages = [];
      this.pendingSave = null;
      this.pendingDownloads.clear();

      if (this.destroyed) return;

      if (this.editorPath !== null) {
        alert('File connection lost. Unsaved edits were not saved.');
      } else {
        this.el.innerHTML = '<div class="file-empty">Connection failed. The file server may not be running.</div>';
      }
    };

    this.ws.onerror = (e) => {
      console.error('[files] WebSocket error:', e);
    };

    console.log('[files] Connecting to:', options.wsUrl);

    // Global keyboard handler
    this.keyHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (this.editorPath !== null) {
          this.closeEditor();
        } else {
          this.close();
        }
        e.preventDefault();
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 's' && this.editorPath !== null) {
        this.saveFile();
        e.preventDefault();
      }
    };
    document.addEventListener('keydown', this.keyHandler);
  }

  private handleMessage(msg: { type: string; [key: string]: unknown }): void {
    switch (msg.type) {
      case 'auth_ok':
        this.authenticated = true;
        this.flushPending();
        this.navigate('');
        break;
      case 'file_list':
        this.handleFileList(msg as unknown as { path: string; entries: FileEntry[] });
        break;
      case 'file_content':
        this.handleFileContent(msg as unknown as { path: string; content: string; size: number });
        break;
      case 'file_ok':
        this.handleFileOk(msg as unknown as { path: string; action: string });
        break;
      case 'file_error':
        this.handleFileError(msg as unknown as { path: string; code: string; message: string });
        break;
      case 'error':
        if ((msg as { code?: string }).code === 'auth_failed') {
          this.el.innerHTML = '<div class="file-empty">Authentication failed</div>';
        }
        break;
    }
  }

  private sendMsg(msg: Record<string, unknown>): boolean {
    if (this.closed || this.ws.readyState === WebSocket.CLOSING || this.ws.readyState === WebSocket.CLOSED) {
      alert('File connection is closed. Reopen the file manager and try again.');
      return false;
    }

    const data = JSON.stringify(msg);
    if (this.ws.readyState === WebSocket.OPEN && this.authenticated) {
      this.ws.send(data);
      return true;
    }

    this.pendingMessages.push(data);
    return true;
  }

  private flushPending(): void {
    if (this.closed || this.ws.readyState !== WebSocket.OPEN) {
      this.pendingMessages = [];
      return;
    }

    for (const msg of this.pendingMessages) {
      this.ws.send(msg);
    }
    this.pendingMessages = [];
  }

  close(): void {
    if (this.editorDirty) {
      if (!confirm('You have unsaved changes. Discard?')) return;
    }
    this.destroy();
    this.onClose();
  }

  destroy(): void {
    this.destroyed = true;
    this.closed = true;
    document.removeEventListener('keydown', this.keyHandler);
    if (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING) {
      this.ws.close();
    }
    this.el.remove();
  }

  private navigate(path: string): void {
    this.currentPath = path;
    this.sendMsg({ type: 'file_list', path });
  }

  private openFile(path: string): void {
    this.sendMsg({ type: 'file_read', path });
  }

  private saveFile(): void {
    if (!this.editorPath) return;
    const textarea = this.el.querySelector('.file-editor-textarea') as HTMLTextAreaElement;
    if (textarea) {
      this.editorContent = textarea.value;
    }

    if (this.pendingSave) {
      return;
    }

    const path = this.editorPath;
    const content = this.editorContent;
    if (this.sendMsg({ type: 'file_write', path, content })) {
      this.pendingSave = { path, content };
    }
  }

  private deleteEntry(path: string, isDir: boolean): void {
    const name = path.split('/').pop() || path;
    if (!confirm(`Delete ${isDir ? 'directory' : 'file'} "${name}"?`)) return;
    this.sendMsg({ type: 'file_delete', path });
  }

  private createFile(): void {
    const name = prompt('New file name:');
    if (!name) return;
    const path = this.currentPath ? `${this.currentPath}/${name}` : name;
    this.sendMsg({ type: 'file_write', path, content: '' });
  }

  private createFolder(): void {
    const name = prompt('New folder name:');
    if (!name) return;
    const path = this.currentPath ? `${this.currentPath}/${name}` : name;
    this.sendMsg({ type: 'file_mkdir', path });
  }

  private uploadFile(): void {
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = true;
    input.addEventListener('change', () => {
      if (!input.files) return;
      for (const file of Array.from(input.files)) {
        if (file.size > 1024 * 1024) {
          alert(`File "${file.name}" is too large (max 1MB)`);
          continue;
        }
        const reader = new FileReader();
        reader.onload = () => {
          const content = reader.result as string;
          const path = this.currentPath ? `${this.currentPath}/${file.name}` : file.name;
          this.sendMsg({ type: 'file_write', path, content });
        };
        reader.readAsText(file);
      }
    });
    input.click();
  }

  private downloadFile(path: string, content: string): void {
    const name = path.split('/').pop() || 'file';
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    a.click();
    URL.revokeObjectURL(url);
  }

  private closeEditor(): void {
    if (this.editorDirty) {
      if (!confirm('You have unsaved changes. Discard?')) return;
    }
    this.editorPath = null;
    this.editorContent = '';
    this.editorDirty = false;
    this.originalContent = '';
    this.renderBrowser();
  }

  private formatSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  // WebSocket message handlers

  private handleFileList(msg: { path: string; entries: FileEntry[] }): void {
    this.entries = msg.entries;
    // Sort: directories first, then alphabetical
    this.entries.sort((a, b) => {
      if (a.dir !== b.dir) return a.dir ? -1 : 1;
      return a.name.localeCompare(b.name);
    });
    this.currentPath = msg.path;
    if (this.editorPath === null) {
      this.renderBrowser();
    }
  }

  private handleFileContent(msg: { path: string; content: string; size: number }): void {
    if (this.pendingDownloads.has(msg.path)) {
      this.pendingDownloads.delete(msg.path);
      this.downloadFile(msg.path, msg.content);
      return;
    }

    this.editorPath = msg.path;
    this.editorContent = msg.content;
    this.originalContent = msg.content;
    this.pendingSave = null;
    this.editorDirty = false;
    this.renderEditor();
  }

  private handleFileOk(msg: { path: string; action: string }): void {
    if (msg.action === 'write' && this.editorPath === msg.path) {
      const savedContent = this.pendingSave?.path === msg.path ? this.pendingSave.content : this.editorContent;
      this.pendingSave = null;
      this.originalContent = savedContent;

      const textarea = this.el.querySelector('.file-editor-textarea') as HTMLTextAreaElement;
      if (textarea) {
        this.editorContent = textarea.value;
      }

      const title = this.el.querySelector('.file-editor-title');
      if (title) {
        const name = this.editorPath.split('/').pop() || this.editorPath;
        this.updateDirty(title, name);
      } else {
        this.editorDirty = this.editorContent !== this.originalContent;
      }
    }
    // Refresh listing
    this.sendMsg({ type: 'file_list', path: this.currentPath });
  }

  private handleFileError(msg: { path: string; code: string; message: string }): void {
    if (this.pendingSave?.path === msg.path) {
      this.pendingSave = null;
    }
    this.pendingDownloads.delete(msg.path);
    alert(`File error: ${msg.message}`);
  }

  // Rendering

  private renderBrowser(): void {
    const parts = this.currentPath ? this.currentPath.split('/') : [];

    let breadcrumb = '<span class="file-breadcrumb-item" data-path="">root</span>';
    let cumulative = '';
    for (const part of parts) {
      cumulative = cumulative ? `${cumulative}/${part}` : part;
      breadcrumb += ` <span class="file-breadcrumb-sep">/</span> `;
      breadcrumb += `<span class="file-breadcrumb-item" data-path="${escapeAttr(cumulative)}">${escapeHtml(part)}</span>`;
    }

    const entriesHtml = this.entries.map(entry => {
      const entryPath = this.currentPath ? `${this.currentPath}/${entry.name}` : entry.name;
      const icon = entry.dir ? '\u{1F4C1}' : '\u{1F4C4}';
      return `
        <div class="file-entry" data-path="${escapeAttr(entryPath)}" data-dir="${entry.dir}">
          <span class="file-entry-icon">${icon}</span>
          <span class="file-entry-name">${escapeHtml(entry.name)}</span>
          <span class="file-entry-size">${entry.dir ? '' : this.formatSize(entry.size)}</span>
          <div class="file-entry-actions">
            ${!entry.dir ? `<button class="file-btn-sm file-download-btn" data-path="${escapeAttr(entryPath)}" title="Download">&#x2B07;</button>` : ''}
            <button class="file-btn-sm file-delete-btn" data-path="${escapeAttr(entryPath)}" data-dir="${entry.dir}" title="Delete">&#x2715;</button>
          </div>
        </div>
      `;
    }).join('');

    this.el.innerHTML = `
      <div class="file-browser">
        <div class="file-toolbar">
          <div class="file-breadcrumb">${breadcrumb}</div>
          <div class="file-toolbar-actions">
            <button class="file-btn" id="file-new-file">New File</button>
            <button class="file-btn" id="file-new-folder">New Folder</button>
            <button class="file-btn" id="file-upload">Upload</button>
            <button class="file-btn file-btn-close" id="file-close">Close</button>
          </div>
        </div>
        <div class="file-entries">
          ${this.currentPath ? `
            <div class="file-entry file-entry-parent" data-path="${escapeAttr(parentPath(this.currentPath))}" data-dir="true">
              <span class="file-entry-icon">\u{1F4C2}</span>
              <span class="file-entry-name">..</span>
              <span class="file-entry-size"></span>
              <div class="file-entry-actions"></div>
            </div>
          ` : ''}
          ${entriesHtml || '<div class="file-empty">Empty directory</div>'}
        </div>
      </div>
    `;

    // Event listeners
    this.el.querySelector('#file-new-file')?.addEventListener('click', () => this.createFile());
    this.el.querySelector('#file-new-folder')?.addEventListener('click', () => this.createFolder());
    this.el.querySelector('#file-upload')?.addEventListener('click', () => this.uploadFile());
    this.el.querySelector('#file-close')?.addEventListener('click', () => this.close());

    // Breadcrumb navigation
    this.el.querySelectorAll('.file-breadcrumb-item').forEach(item => {
      item.addEventListener('click', () => {
        const path = (item as HTMLElement).dataset.path || '';
        this.navigate(path);
      });
    });

    // Entry click (navigate or open)
    this.el.querySelectorAll('.file-entry').forEach(entry => {
      const el = entry as HTMLElement;
      // Click on entry name/icon to navigate or open
      const nameEl = el.querySelector('.file-entry-name');
      const iconEl = el.querySelector('.file-entry-icon');
      const handler = () => {
        const path = el.dataset.path || '';
        if (el.dataset.dir === 'true') {
          this.navigate(path);
        } else {
          this.openFile(path);
        }
      };
      nameEl?.addEventListener('click', handler);
      iconEl?.addEventListener('click', handler);
    });

    // Delete buttons
    this.el.querySelectorAll('.file-delete-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const el = btn as HTMLElement;
        this.deleteEntry(el.dataset.path || '', el.dataset.dir === 'true');
      });
    });

    // Download buttons
    this.el.querySelectorAll('.file-download-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const path = (btn as HTMLElement).dataset.path || '';
        this.pendingDownloads.add(path);
        if (!this.sendMsg({ type: 'file_read', path })) {
          this.pendingDownloads.delete(path);
        }
      });
    });
  }

  private renderEditor(): void {
    if (!this.editorPath) return;
    const name = this.editorPath.split('/').pop() || this.editorPath;

    this.el.innerHTML = `
      <div class="file-editor">
        <div class="file-editor-toolbar">
          <span class="file-editor-title">${escapeHtml(name)}</span>
          <div class="file-editor-actions">
            <button class="file-btn" id="file-editor-save">Save (Ctrl+S)</button>
            <button class="file-btn" id="file-editor-download">Download</button>
            <button class="file-btn file-btn-close" id="file-editor-close">Close</button>
          </div>
        </div>
        <textarea class="file-editor-textarea" spellcheck="false">${escapeHtml(this.editorContent)}</textarea>
      </div>
    `;

    const textarea = this.el.querySelector('.file-editor-textarea') as HTMLTextAreaElement;
    const titleEl = this.el.querySelector('.file-editor-title')!;

    // Focus textarea
    textarea.focus();

    // Tab key inserts 2 spaces
    textarea.addEventListener('keydown', (e) => {
      if (e.key === 'Tab') {
        e.preventDefault();
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        textarea.value = textarea.value.substring(0, start) + '  ' + textarea.value.substring(end);
        textarea.selectionStart = textarea.selectionEnd = start + 2;
        this.editorContent = textarea.value;
        this.updateDirty(titleEl, name);
      }
    });

    // Track changes
    textarea.addEventListener('input', () => {
      this.editorContent = textarea.value;
      this.updateDirty(titleEl, name);
    });

    // Buttons
    this.el.querySelector('#file-editor-save')?.addEventListener('click', () => this.saveFile());
    this.el.querySelector('#file-editor-download')?.addEventListener('click', () => {
      this.downloadFile(this.editorPath!, this.editorContent);
    });
    this.el.querySelector('#file-editor-close')?.addEventListener('click', () => this.closeEditor());
  }

  private updateDirty(titleEl: Element, name: string): void {
    this.editorDirty = this.editorContent !== this.originalContent;
    titleEl.textContent = this.editorDirty ? `${name} *` : name;
  }
}

function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

function escapeAttr(text: string): string {
  return text.replace(/&/g, '&amp;').replace(/"/g, '&quot;');
}

function parentPath(path: string): string {
  const idx = path.lastIndexOf('/');
  return idx > 0 ? path.substring(0, idx) : '';
}

// CSS styles for file manager
const fileStyles = `
.file-manager {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: #0a0a0a;
  display: flex;
  flex-direction: column;
  font-family: "JetBrains Mono", "Fira Code", monospace;
  color: #c0c0c0;
  z-index: 100;
}

.file-browser, .file-editor {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.file-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 16px;
  background: #1a1a1a;
  border-bottom: 1px solid #333;
  flex-shrink: 0;
  flex-wrap: wrap;
  gap: 8px;
}

.file-breadcrumb {
  display: flex;
  align-items: center;
  gap: 2px;
  font-size: 13px;
  flex-wrap: wrap;
}

.file-breadcrumb-item {
  color: #00cd00;
  cursor: pointer;
  padding: 2px 4px;
  border-radius: 3px;
}

.file-breadcrumb-item:hover {
  background: rgba(0, 205, 0, 0.15);
}

.file-breadcrumb-sep {
  color: #555;
}

.file-toolbar-actions {
  display: flex;
  gap: 8px;
}

.file-btn {
  padding: 5px 12px;
  background: #2a2a2a;
  border: 1px solid #444;
  color: #c0c0c0;
  border-radius: 4px;
  cursor: pointer;
  font-family: inherit;
  font-size: 12px;
}

.file-btn:hover {
  background: #333;
  border-color: #555;
}

.file-btn-close {
  color: #888;
}

.file-btn-close:hover {
  color: #ff4444;
  border-color: #ff4444;
}

.file-btn-sm {
  padding: 2px 6px;
  background: transparent;
  border: none;
  color: #666;
  cursor: pointer;
  font-size: 12px;
  border-radius: 3px;
}

.file-btn-sm:hover {
  color: #c0c0c0;
  background: rgba(255, 255, 255, 0.1);
}

.file-delete-btn:hover {
  color: #ff4444 !important;
}

.file-entries {
  flex: 1;
  overflow-y: auto;
  padding: 8px 0;
}

.file-entry {
  display: flex;
  align-items: center;
  padding: 6px 16px;
  cursor: default;
  gap: 8px;
}

.file-entry:hover {
  background: rgba(255, 255, 255, 0.05);
}

.file-entry-icon {
  cursor: pointer;
  font-size: 14px;
  width: 20px;
  text-align: center;
}

.file-entry-name {
  flex: 1;
  cursor: pointer;
  font-size: 13px;
}

.file-entry-name:hover {
  color: #00cd00;
}

.file-entry-size {
  color: #666;
  font-size: 12px;
  min-width: 70px;
  text-align: right;
}

.file-entry-actions {
  display: flex;
  gap: 4px;
  opacity: 0;
  transition: opacity 0.15s;
}

.file-entry:hover .file-entry-actions {
  opacity: 1;
}

.file-empty {
  color: #555;
  text-align: center;
  padding: 40px;
  font-size: 13px;
}

.file-editor-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 16px;
  background: #1a1a1a;
  border-bottom: 1px solid #333;
  flex-shrink: 0;
}

.file-editor-title {
  font-size: 13px;
  color: #00cd00;
}

.file-editor-actions {
  display: flex;
  gap: 8px;
}

.file-editor-textarea {
  flex: 1;
  background: #0a0a0a;
  color: #c0c0c0;
  border: none;
  padding: 12px 16px;
  font-family: "JetBrains Mono", "Fira Code", monospace;
  font-size: 13px;
  line-height: 1.5;
  resize: none;
  outline: none;
  tab-size: 2;
}

.file-entries::-webkit-scrollbar,
.file-editor-textarea::-webkit-scrollbar {
  width: 8px;
}

.file-entries::-webkit-scrollbar-track,
.file-editor-textarea::-webkit-scrollbar-track {
  background: #0a0a0a;
}

.file-entries::-webkit-scrollbar-thumb,
.file-editor-textarea::-webkit-scrollbar-thumb {
  background: #333;
  border-radius: 4px;
}

.file-entries::-webkit-scrollbar-thumb:hover,
.file-editor-textarea::-webkit-scrollbar-thumb:hover {
  background: #444;
}

@media (max-width: 768px) {
  .file-toolbar {
    padding: 6px 12px;
  }
  .file-entry {
    padding: 8px 12px;
  }
  .file-entry-actions {
    opacity: 1;
  }
}
`;

let fileStylesInjected = false;
export function injectFileStyles(): void {
  if (fileStylesInjected) return;
  const style = document.createElement('style');
  style.textContent = fileStyles;
  document.head.appendChild(style);
  fileStylesInjected = true;
}
